import { BALLOON_RADIUS, standingGroundY } from "../game/constants";
import type { BalloonType } from "../game/types";

export const BALLOON_COLORS: Record<BalloonType, { a: string; b: string; c: string; seam: string }> = {
  blue: { a: "#2f7dff", b: "#7eb6ff", c: "#c4e2ff", seam: "#1f5fcc" },
  green: { a: "#22b056", b: "#7ae08f", c: "#d4f7c8", seam: "#1a8a42" },
  red: { a: "#ff2d3a", b: "#ff8080", c: "#ffd0d0", seam: "#c41e3a" },
  gold: { a: "#f0b429", b: "#ffe566", c: "#fff8d0", seam: "#b8860b" },
};

export interface BalloonGraphicOptions {
  colorAssist?: boolean;
  highContrast?: boolean;
  reduceMotion?: boolean;
  shadow?: boolean;
}

/** Shared gold/color balloon art used in play and in the Beach Pop logo. */
export function drawBalloonGraphic(
  ctx: CanvasRenderingContext2D,
  type: BalloonType,
  radius: number,
  spin: number,
  options: BalloonGraphicOptions = {},
): void {
  const pal = BALLOON_COLORS[type];
  const r = radius;
  const colorAssist = Boolean(options.colorAssist);
  const highContrast = Boolean(options.highContrast);
  const reduceMotion = Boolean(options.reduceMotion);
  const shadow = options.shadow !== false;

  if (shadow) {
    ctx.fillStyle = "rgba(40, 30, 16, 0.18)";
    ctx.beginPath();
    ctx.ellipse(3, r * 0.92, r * 0.72, r * 0.2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.clip();
  const panels = type === "gold" ? 8 : 6;
  for (let i = 0; i < panels; i += 1) {
    const a0 = spin + (i * Math.PI * 2) / panels - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r * 1.15, a0, a0 + Math.PI / panels);
    ctx.closePath();
    ctx.fillStyle = i % 2 === 0 ? pal.a : pal.b;
    ctx.fill();
  }
  const volume = ctx.createRadialGradient(-r * 0.28, -r * 0.32, r * 0.1, 0, 0, r);
  volume.addColorStop(0, "rgba(255,255,255,0.38)");
  volume.addColorStop(0.45, "rgba(255,255,255,0.05)");
  volume.addColorStop(1, "rgba(20, 30, 40, 0.18)");
  ctx.fillStyle = volume;
  ctx.fillRect(-r, -r, r * 2, r * 2);
  ctx.strokeStyle = pal.seam;
  ctx.lineWidth = Math.max(1, r * 0.045);
  ctx.globalAlpha = 0.7;
  for (let i = 0; i < panels; i += 1) {
    const a = spin + (i * Math.PI * 2) / panels - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.strokeStyle = highContrast ? "#111" : pal.seam;
  ctx.lineWidth = highContrast ? Math.max(2.4, r * 0.1) : Math.max(1.8, r * 0.08);
  ctx.stroke();

  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.beginPath();
  ctx.ellipse(-r * 0.28, -r * 0.34, r * 0.2, r * 0.28, -0.5, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = pal.seam;
  ctx.beginPath();
  ctx.arc(0, 0, Math.max(2.4, r * 0.09), 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = pal.c;
  ctx.beginPath();
  ctx.arc(0, 0, Math.max(1.2, r * 0.04), 0, Math.PI * 2);
  ctx.fill();

  if (type === "gold") {
    ctx.strokeStyle = reduceMotion ? "rgba(255,255,220,0.45)" : "rgba(255,255,220,0.8)";
    ctx.lineWidth = Math.max(1.8, r * 0.09);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.7, spin, spin + (reduceMotion ? 0.5 : 1.1));
    ctx.stroke();
    ctx.strokeStyle = "rgba(255, 210, 80, 0.45)";
    ctx.lineWidth = Math.max(2.2, r * 0.11);
    ctx.beginPath();
    ctx.arc(0, 0, r + Math.max(2, r * 0.1), 0, Math.PI * 2);
    ctx.stroke();
  }

  drawBalloonSymbol(ctx, type, r, colorAssist);
}

function drawBalloonSymbol(ctx: CanvasRenderingContext2D, type: BalloonType, radius: number, colorAssist: boolean): void {
  ctx.strokeStyle = colorAssist ? "#111" : "#fff";
  ctx.fillStyle = colorAssist ? "#111" : "#fff";
  ctx.lineWidth = Math.max(1.6, radius * 0.09);
  const s = radius * 0.38;
  if (type === "blue") {
    ctx.beginPath();
    ctx.moveTo(-s, 0);
    ctx.quadraticCurveTo(0, s * 0.7, s, 0);
    ctx.quadraticCurveTo(0, -s * 0.2, -s, 0);
    ctx.stroke();
  } else if (type === "green") {
    ctx.beginPath();
    for (let i = 0; i < 5; i += 1) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
      const x = Math.cos(a) * s;
      const y = Math.sin(a) * s;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  } else if (type === "red") {
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.lineTo(s, 0);
    ctx.lineTo(0, s);
    ctx.lineTo(-s, 0);
    ctx.closePath();
    ctx.stroke();
  } else {
    ctx.beginPath();
    for (let i = 0; i < 8; i += 1) {
      const a = (i * Math.PI) / 4;
      const rr = i % 2 === 0 ? s : s * 0.45;
      const x = Math.cos(a) * rr;
      const y = Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }
}

/** Square PNG of the in-game balloon, uniformly scaled with margin so the rim is not clipped. */
export function goldBalloonDataUrl(pixelSize: number, options: BalloonGraphicOptions = {}): string {
  const size = Math.max(64, Math.round(pixelSize));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.scale(1, 1);
  const radius = size * 0.4;
  drawBalloonGraphic(ctx, "gold", radius, 0.55, { ...options, shadow: true });
  ctx.restore();
  return canvas.toDataURL("image/png");
}

export class Balloon {
  type: BalloonType;
  x: number;
  y: number;
  baseX: number;
  radius: number;
  t = 0;
  duration: number;
  peak: number;
  alive = true;
  popping = false;
  popScale = 1;
  popSquash = 1;
  private popT = 0;
  wobble = Math.random() * Math.PI * 2;
  spin = Math.random() * Math.PI * 2;

  constructor(type: BalloonType, x: number, duration: number, peak: number) {
    this.type = type;
    this.x = x;
    this.baseX = x;
    this.y = standingGroundY(x) - BALLOON_RADIUS[type];
    this.radius = BALLOON_RADIUS[type];
    this.duration = duration;
    this.peak = peak;
  }

  get hittable(): boolean {
    return this.alive && !this.popping;
  }

  pop(): void {
    if (this.popping) return;
    this.popping = true;
    this.popT = 0;
  }

  update(dt: number, reduceMotion: boolean): boolean {
    if (!this.alive) return false;
    if (this.popping) {
      this.popT += dt;
      const u = Math.min(1, this.popT / 0.16);
      this.popScale = 1 + Math.sin(u * Math.PI) * 0.32;
      this.popSquash = 1 - Math.sin(u * Math.PI) * 0.28;
      if (this.popT >= 0.16) this.alive = false;
      return false;
    }
    this.t += dt;
    const u = Math.min(1, this.t / this.duration);
    this.y = standingGroundY(this.x) - this.radius - Math.sin(Math.PI * u) * this.peak;
    if (this.type === "gold") {
      this.x = this.baseX + Math.sin(this.t * 2.2 + this.wobble) * 36;
    }
    if (!reduceMotion) this.spin += dt * (0.85 + (this.type === "gold" ? 0.55 : 0));
    if (u >= 1) {
      this.alive = false;
      return true;
    }
    return false;
  }

  draw(ctx: CanvasRenderingContext2D, colorAssist: boolean, highContrast: boolean, reduceMotion: boolean): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(this.popScale, this.popScale * this.popSquash);
    drawBalloonGraphic(ctx, this.type, this.radius, this.spin, { colorAssist, highContrast, reduceMotion, shadow: true });
    ctx.restore();
  }
}
